import type { Schemas } from "@neio/contracts";
import { BadgeCheck, Star } from "lucide-react";
import { useState } from "react";

import type { ReplacementCandidate } from "../lib/api";
import { SourceLink } from "./SourceLink";

export type ApprovedListStatus = Schemas["ApprovedVendorListStatus"];

/**
 * Whether the approved-vendor flags mean anything. With no list loaded every option is unflagged
 * because there was no list, not because no maker is approved, and the analyst has to know that.
 */
export function ApprovedListNote({ status }: { status: ApprovedListStatus | null | undefined }) {
  if (!status) return null;
  if (status.loaded) {
    const at = status.loaded_at ? ` (file dated ${status.loaded_at.slice(0, 10)})` : "";
    return (
      <p className="muted" style={{ fontSize: 13 }}>
        <BadgeCheck size={14} aria-hidden="true" style={{ verticalAlign: -2 }} /> Approved vendor list
        loaded: {status.rows ?? 0} vendors{at}. Approved makers are listed first within each resolution.
      </p>
    );
  }
  return (
    <p className="muted" style={{ fontSize: 13 }} role="note">
      No approved vendor list is loaded on this environment, so no option is flagged.
      {status.problem ? ` ${status.problem}.` : ""}
    </p>
  );
}

/** The SD-22 Table 9 resolutions, in the words an analyst uses for them. */
export const OUTCOME_LABELS: Record<string, string> = {
  approved_item: "approved alternate",
  life_of_need_buy: "life of need buy",
  repair_or_reclamation: "repair or reclamation",
  extension_of_production: "extended production or support",
  simple_substitute: "drop-in successor",
  complex_substitute: "successor with other changes",
  new_item_or_source: "new item or source needed",
  next_higher_assembly_redesign: "redesign of the assembly",
  system_redesign: "system redesign",
};

/**
 * Replacement options as the market search ranked them.
 *
 * Shown in the order the API returns, which is the SD-22 cost order and then how much else must
 * change. Re-sorting here would put a second ranking rule in the client that the export and the
 * audit trail do not share.
 */
export function MarketResults({ candidates }: { candidates: ReplacementCandidate[] }) {
  const [starred, setStarred] = useState<Set<number>>(new Set());

  return (
    <div className="market-grid">
      {candidates.map((c, i) => (
        <div key={`${c.source_url}-${i}`} className="optcard">
          <div className="top">
            <span className="name">{c.part_number ?? c.description ?? "unnamed option"}</span>
            <span className="pill info">{OUTCOME_LABELS[c.outcome] ?? c.outcome.replace(/_/g, " ")}</span>
            <span className="muted" style={{ fontSize: 12 }}>{c.authority.replace(/_/g, " ")}</span>
            {c.on_approved_vendor_list && (
              <span className="pill ok"><BadgeCheck size={12} aria-hidden="true" style={{ verticalAlign: -2 }} /> on approved vendor list</span>
            )}
            <button className={`star ${starred.has(i) ? "on" : ""}`}
                    onClick={() => {
                      const s = new Set(starred);
                      if (s.has(i)) s.delete(i); else s.add(i);
                      setStarred(s);
                    }} aria-label="Shortlist">
              <Star size={18} fill={starred.has(i) ? "currentColor" : "none"} />
            </button>
          </div>

          {c.part_number && c.description && (
            <p className="muted" style={{ fontSize: 13, marginTop: 6 }}>{c.description}</p>
          )}

          <div className="facts">
            {c.manufacturer && <div><b>Maker</b>{c.manufacturer}</div>}
            <div>
              <b>Also changes</b>
              {c.collateral.length ? c.collateral.join(", ") : "nothing else named"}
            </div>
            {c.available_until && <div><b>Available until</b>{c.available_until}</div>}
          </div>

          <blockquote style={{ margin: "10px 0 6px", fontSize: 14 }}>
            {c.quote ? `"${c.quote}"` : <span className="muted">no sentence quoted</span>}
          </blockquote>
          <div style={{ fontSize: 12 }}><SourceLink url={c.source_url} /></div>
        </div>
      ))}
    </div>
  );
}
